import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

function statusColor(s) {
  if (s === 'open') return 'info';
  if (s === 'in_progress') return 'primary';
  if (s === 'waiting_customer') return 'warning';
  if (s === 'resolved') return 'success';
  if (s === 'closed') return 'default';
  return 'default';
}

function priorityColor(p) {
  if (p === 'urgent') return 'error';
  if (p === 'high') return 'warning';
  return 'default';
}

export default function TenantSupportTicketsListPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [priorityInput, setPriorityInput] = useState('');
  const [appliedPriority, setAppliedPriority] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [sort, setSort] = useState('opened_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedPriority) params.set('priority', appliedPriority);
    if (appliedDateFrom) params.set('dateFrom', appliedDateFrom);
    if (appliedDateTo) params.set('dateTo', appliedDateTo);
    try {
      const res = await tenantApiFetch(`/api/tenant/support-tickets?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load tickets');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedPriority, appliedDateFrom, appliedDateTo, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedPriority(priorityInput);
    setAppliedDateFrom(dateFromInput.trim());
    setAppliedDateTo(dateToInput.trim());
  };

  const rows = data?.tickets || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Support tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open and track requests for your organization.
          </Typography>
        </Box>
        <TenantCan permission="tenant.support_tickets.create">
          <Button variant="contained" startIcon={<AddIcon />} component={Link} to="/tenant/support/new">
            New ticket
          </Button>
        </TenantCan>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Subject, number, description, email…"
            sx={{ minWidth: 220 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Priority</InputLabel>
            <Select label="Priority" value={priorityInput} onChange={(e) => setPriorityInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Opened from"
            type="date"
            value={dateFromInput}
            onChange={(e) => setDateFromInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Opened to"
            type="date"
            value={dateToInput}
            onChange={(e) => setDateToInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="opened_at">Opened</MenuItem>
              <MenuItem value="updated_at">Updated</MenuItem>
              <MenuItem value="priority">Priority</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="ticket_number">Ticket #</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Newest</MenuItem>
              <MenuItem value="asc">Oldest</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {row.ticket_number || `#${row.id}`}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }} title={row.subject}>
                      {row.subject}
                    </TableCell>
                    <TableCell>{row.requester_email || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.priority} color={priorityColor(row.priority)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status.replace(/_/g, ' ')}
                        color={statusColor(row.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.opened_at ? String(row.opened_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/tenant/support/${row.id}`} size="small" aria-label="View">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <TenantCan permission="tenant.support_tickets.manage">
                        <IconButton
                          size="small"
                          aria-label="Edit"
                          onClick={() => navigate(`/tenant/support/${row.id}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TenantCan>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      No tickets yet.{' '}
                      <TenantCan permission="tenant.support_tickets.create">
                        <Button size="small" component={Link} to="/tenant/support/new">
                          Create one
                        </Button>
                      </TenantCan>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
